import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq, citySeo } from "./cityPricing";

const monaco: ChauffeurPage = {
  slug: "monaco",
  title: "Chauffeur privé à Monaco – VTC 24h/24",
  subtitle:
    "Chauffeur privé en Principauté : Monte-Carlo, Port Hercule, Grand Prix, Yacht Show et liaison aéroport de Nice.",
  keywords: [
    "chauffeur privé monaco",
    "vtc monaco",
    "chauffeur monte-carlo",
    "transfert monaco aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé à Monaco",
    subtitle:
      "Monte-Carlo, Casino, Port Hercule : discrétion et ponctualité en Principauté, 24h/24.",
    keywords: [
      "chauffeur Monaco",
      "VTC Monte-Carlo",
      "chauffeur Principauté",
      "transfert Monaco aéroport Nice",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé à Monaco",
    badges: ["Discrétion absolue", "Liaison aéroport Nice", "Service 24/7"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur en Principauté",
    items: [
      {
        title: "Discrétion de rigueur",
        description:
          "Clientèle privée, dirigeants et résidents : chauffeur en tenue, confidentialité totale.",
      },
      {
        title: "Liaison aéroport de Nice",
        description:
          "Le trajet classique de la Principauté, en berline ou van, avec suivi de vol.",
      },
      {
        title: "Grands événements",
        description:
          "Grand Prix de Monaco, Yacht Show, galas : logistique rodée sur agendas serrés.",
      },
      {
        title: "Maîtrise des accès",
        description:
          "Casino, palaces et Port Hercule : dépose au plus près malgré les fermetures de voirie.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Monaco",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Monaco ↔ Nice Côte d'Azur, coordination possible avec l'héliport.",
      },
      {
        title: "Mise à disposition",
        text: "Chauffeur dédié à l'heure ou à la journée pour rendez-vous d'affaires et soirées.",
      },
      {
        title: "Soirées & galas",
        text: "Casino de Monte-Carlo, Opéra, dîners et événements caritatifs, retour de nuit assuré.",
      },
      {
        title: "Grand Prix & Yacht Show",
        text: "Déplacements optimisés pendant les fermetures de circuit et les pics d'affluence.",
      },
      {
        title: "Accès yachts & palaces",
        text: "Dépose et reprise à Port Hercule et devant les grands hôtels de la Principauté.",
      },
    ],
  },
  locations: {
    heading: "Points clés en Principauté",
    items: [
      {
        name: "Monte-Carlo",
        description: "Casino, Place du Casino, hôtels emblématiques.",
      },
      {
        name: "Port Hercule",
        description: "Quais yachting et zone des grands événements.",
      },
      {
        name: "Le Rocher / Monaco-Ville",
        description: "Palais Princier et vieille ville.",
      },
      {
        name: "La Condamine",
        description: "Marché, centre et accès commerçant.",
      },
      {
        name: "Fontvieille",
        description: "Quartier résidentiel, port et héliport à proximité.",
      },
      {
        name: "Larvotto",
        description: "Plages et résidences en bord de mer.",
      },
    ],
  },
  generic: {
    heading: "Monaco : un territoire dense où la discrétion prime",
    subheading:
      "Voirie en pente, tunnels et événements : un chauffeur qui connaît la Principauté.",
    paragraphs: [
      "Sur 2 km², Monaco superpose tunnels, rampes et parkings souterrains. Un chauffeur qui en maîtrise la voirie évite les détours, dépose au plus près du Casino, d'un palace ou d'un quai de Port Hercule, et reprend sans flottement.",
      "La clientèle de la Principauté — dirigeants, résidents, équipages — attend avant tout discrétion et ponctualité. Chauffeur en tenue, véhicule soigné, aucune approximation : le service se fait oublier.",
      "Pendant le Grand Prix et le Monaco Yacht Show, des artères entières ferment. Nous adaptons les points de prise en charge et les horaires pour tenir vos rendez-vous malgré l'affluence.",
    ],
    bullets: ["Chauffeur en tenue", "Suivi de vol", "Coordination héliport"],
  },
  process: {
    heading: "Réserver un chauffeur à Monaco",
    steps: [
      "Précisez votre adresse en Principauté (hôtel, résidence, quai, héliport).",
      "Nous calons l'itinéraire et l'heure selon l'événement et les fermetures éventuelles.",
      "Le chauffeur se présente en tenue, véhicule soigné, à l'heure convenue.",
      "Transfert direct ou mise à disposition pour enchaîner vos rendez-vous.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Monaco", "MONACO"),
    {
      q: "Quelle est la durée d'un trajet Monaco – aéroport de Nice ?",
      a: "Environ 35 à 45 minutes selon le trafic et l'itinéraire (basse/moyenne corniche ou A8). Le chauffeur suit votre vol et ajuste l'heure de départ.",
    },
    {
      q: "Pouvez-vous coordonner un transfert avec l'héliport de Monaco ?",
      a: "Oui, nous assurons la liaison vers et depuis l'héliport de Fontvieille. Indiquez-le à la réservation pour caler les horaires avec votre vol.",
    },
    {
      q: "Intervenez-vous pendant le Grand Prix ou le Yacht Show ?",
      a: "Oui. Pendant ces périodes, de nombreuses voies ferment ; nous adaptons les points de prise en charge et anticipons les temps de trajet pour rester ponctuels.",
    },
    {
      q: "Le service garantit-il la discrétion pour une clientèle privée ?",
      a: "Oui, c'est notre standard en Principauté : chauffeur en tenue, confidentialité totale et dépose au plus près des palaces, résidences ou quais de Port Hercule.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, adaptés à la clientèle internationale et aux dirigeants de la Principauté.",
    },
    {
      q: "Quels véhicules proposez-vous à Monaco ?",
      a: "Berline Mercedes Classe E et Classe S pour la clientèle affaires et VIP, van 7 places, SUV et minibus VIP pour les groupes et les événements.",
    },
    {
      q: "Faites-vous la longue distance depuis Monaco ?",
      a: "Oui, trajets toute distance : Cannes, Saint-Tropez (env. 2h), l'Italie voisine (Vintimille, Sanremo, Gênes, Milan) et les stations de ski, avec un chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Cannes ⇄ Monaco avec chauffeur, dès 200 €",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Monaco ⇄ Aéroport de Nice, tarif et durée",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Chauffeur Grand Prix de Monaco",
      href: "/chauffeur/grand-prix-de-monaco",
    },
    {
      label: "Chauffeur Monaco Yacht Show",
      href: "/chauffeur/monaco-yacht-show",
    },
    {
      label: "Classe S avec chauffeur",
      href: "/chauffeur/classe-s-avec-chauffeur",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Monaco",
    text: "Aéroport, gala ou Grand Prix : réservez un chauffeur discret en Principauté.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : titre sans prix, page en position 36 à 45.
    ...citySeo("fr", "Monaco", "MONACO", ", VTC Monte-Carlo"),
    canonical: "/chauffeur/monaco",
  },
};

export default monaco;
